import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90 hover:text-primary-foreground focus-visible:ring-primary/20 dark:focus-visible:ring-primary/40 dark:bg-primary/60 dark:text-primary-foreground",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
        span: "",
        "icon-text":
          "min-h-12 px-4 bg-white rounded-lg border-[2px] border-zinc-900 inline-flex justify-center items-center gap-1 text-zinc-900 text-base font-normal tracking-tight",

        new_default:
          "text-white bg-royal-glow-3 hover:bg-royal-glow-3/80 focus-visible:ring-custom-outline/50 disabled:opacity-50 focus-visible:disabled:opacity-60",
        new_secondary:
          "text-royal-glow-2 bg-royal-glow-5 focus-visible:ring-custom-outline/50 disabled:opacity-50 focus-visible:disabled:opacity-60",
        new_destructive:
          "text-white bg-custom-destructive-dark hover:bg-custom-destructive-dark/80 focus-visible:ring-custom-destructive-dark/40 disabled:opacity-50 focus-visible:disabled:opacity-60",
        new_outline:
          "text-black border border-base-border hover:bg-secondary-badge focus-visible:border-custom-outline focus-visible:ring-custom-outline/50 disabled:opacity-50 focus-visible:disabled:opacity-60",
        new_ghost:
          "text-black hover:bg-secondary-badge focus-visible:ring-custom-outline/50 disabled:opacity-50 focus-visible:disabled:opacity-60",
        new_link:
          "text-black hover:underline focus-visible:ring-custom-outline/50 disabled:opacity-50 focus-visible:disabled:opacity-60",
        operational:
          "text-white bg-skyline-2 hover:bg-skyline-2/80 focus-visible:ring-custom-outline/50 disabled:opacity-50 focus-visible:disabled:opacity-60",
      },
      size: {
        default: "h-8 px-4 py-2 text-sm/[20px] font-normal",
        sm: "h-6 rounded-md gap-1.5 px-3 py-2 text-xs/[12px] font-normal",
        lg: "h-10 rounded-md px-6 py-2 text-sm/[20px] font-normal",
        xl: "h-12 rounded-md px-8",
        icon: "size-9",
        icon_sm: "size-8",
        icon_lg: "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
